import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import type { JSX, MouseEvent, PropsWithChildren } from 'react'
import type { PlanId } from '~/lib/graphql/scalars'
import { useReserveNavigate } from '~/navigation'
import { css, cva } from '~/styled-system/css'
import { Button } from '~/ui'
import { Dialog } from '~/ui/Dialog'

type useReserveButton = (
  planId: PlanId,
  hasNoInventory?: boolean
) => {
  top: JSX.Element | null
  bottom: JSX.Element | null
  dialog: JSX.Element | null
}

export const useReserveButton: useReserveButton =
  import.meta.env.VITE_MODE === 'ppg' ? usePpgReserveButton : useIkyuReserveButton

function usePpgReserveButton(planId: PlanId, hasNoInventory?: boolean) {
  const toReserve = useReserve(planId)
  const [showBottom, setShowBottom] = useState(false)

  const ref = useRef<HTMLDialogElement>(null)
  const open = useCallback(() => {
    ref.current?.showModal()
  }, [])

  const top = (
    <PpgTop toReserve={toReserve} toggleBottom={setShowBottom} hasNoInventory={hasNoInventory} />
  )
  const dialog = <PpgDataShareAgreementDialog ref={ref} />
  const bottom = showBottom ? (
    <Bottom open={open} toReserve={toReserve} hasNoInventory={hasNoInventory} />
  ) : null

  return { top, dialog, bottom } as const
}

function useIkyuReserveButton(planId: PlanId, hasNoInventory?: boolean) {
  const toReserve = useReserve(planId)
  const bottom = <Bottom toReserve={toReserve} hasNoInventory={hasNoInventory} />
  return { top: null, dialog: null, bottom } as const
}

function useReserve(planId: PlanId) {
  const navigate = useReserveNavigate()
  return useCallback(() => {
    navigate({
      type: 'reserve',
      planId,
    })
  }, [navigate, planId])
}

type Props = {
  toReserve: () => void
  open?: () => void
  hasNoInventory?: boolean
}

function PpgTop({
  toReserve,
  hasNoInventory,
  toggleBottom,
}: Props & { toggleBottom: (show: boolean) => void }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current === null) {
      return
    }
    const observer = new IntersectionObserver((entries) => {
      toggleBottom(!entries[0].isIntersecting)
    })
    observer.observe(ref.current)
    return () => {
      observer.disconnect()
    }
  }, [toggleBottom])

  return (
    <div ref={ref} className={css({ spaceY: '2', py: '4' })}>
      <DataShareAgreement>
        <Reserve toReserve={toReserve} hasNoInventory={hasNoInventory} />
      </DataShareAgreement>
    </div>
  )
}

const styles = cva({
  base: {
    color: 'gray.800',
  },
  variants: {
    fontSize: {
      xs: {
        fontSize: 'xs',
        lineHeight: 'tight',
      },
      base: {
        fontSize: 'base',
      },
    },
  },
  defaultVariants: {
    fontSize: 'xs',
  },
})

function DataShareAgreement({
  children,
  fontSize,
}: PropsWithChildren<{ fontSize?: 'xs' | 'base' }>) {
  return (
    <>
      <p className={styles({ fontSize })}>
        予約入力画面へ進む際、お客様を識別するための情報とメインメールアドレスを一休に提供します。一休はこれらの情報を
        <a
          href="https://www.ikyu.co.jp/policy/"
          target="_blank"
          rel="noreferrer"
          className={css({
            color: 'link',
            transition: '[opacity 0.2s]',
            _hover: { opacity: '75' },
          })}
        >
          個人情報保護方針
        </a>
        に従って取り扱います。PayPay（株）はPayPay決済のため、PayPay（残高）等・PayPayポイント関連情報を一休に提供します。未利用の方は事前同意となります。
      </p>
      {children}
      <div
        className={css({
          fontSize: 'sm',
          color: 'gray.700',
          lineHeight: 'tight',
          textAlign: 'center',
        })}
      >
        プラン提供：株式会社 一休
      </div>
    </>
  )
}

function Bottom({ open, toReserve, hasNoInventory }: Props) {
  return (
    <div
      className={css({
        position: 'sticky',
        bottom: '0',
        left: '0',
        z: '10',
        w: 'full',
        bgColor: 'white',
        p: '4',
        shadow: 'outline.lg',
        spaceY: '2',
      })}
    >
      <ReserveNotice open={open} />
      <Reserve toReserve={toReserve} hasNoInventory={hasNoInventory} />
    </div>
  )
}

const ReserveNotice = import.meta.env.VITE_MODE === 'ppg' ? PpgReserveNotice : () => null

function PpgReserveNotice({ open }: { open?: () => void }) {
  return (
    <p className={css({ fontSize: 'sm', textAlign: 'center' })}>
      予約に進む前に必ず
      <button
        type="button"
        onClick={open}
        className={css({
          color: 'link',
          transition: '[opacity 0.2s]',
          _hover: { opacity: '75' },
        })}
      >
        こちら
      </button>
      をご確認ください
    </p>
  )
}

const PpgDataShareAgreementDialog = forwardRef<HTMLDialogElement, {}>(
  function PpgDataShareAgreementDialog(_, ref) {
    const content = useRef<HTMLDivElement>(null)

    const close = useCallback(
      (e: MouseEvent<HTMLDialogElement>) => {
        if (
          ref !== null &&
          'current' in ref &&
          e.target instanceof Node &&
          !content.current?.contains(e.target)
        ) {
          ref.current?.close()
        }
      },
      [ref]
    )

    return (
      <Dialog
        ref={ref}
        onClick={close}
        css={css.raw({ mx: '[auto]', maxWidth: '[min(480px,90vw)]' })}
      >
        {/* autoFocus と tabIndex で DataShareAgreement 内のリンクに自動でフォーカスしないように */}
        <div className={css({ p: '4', spaceY: '3' })} ref={content} tabIndex={-1} autoFocus>
          <DataShareAgreement fontSize="base" />
        </div>
      </Dialog>
    )
  }
)

function Reserve({ toReserve, hasNoInventory }: Props) {
  return (
    <Button size="lg" color="secondary" spread disabled={hasNoInventory} onClick={toReserve}>
      {hasNoInventory ? '現在、予約可能日はございません' : 'このプランを予約する'}
    </Button>
  )
}
